import type { ReactElement } from 'react';
import { useInvalidateOrders } from '@/entities/order';
import { useInvalidatePayments, usePaymentDetailQuery } from '@/entities/payment';
import { RefundForm, useCancelPayment, useRetryPaymentCancel } from '@/features/payment-refund';
import { formatDateTime, formatWon } from '@/shared/lib';
import { Button, ContentCard, EmptyState, detailListStyles as styles } from '@/shared/ui';
import { PaymentCancelsTable } from './payment-cancels-table';
import { PaymentDetailHeader } from './payment-detail-header';

interface PaymentDetailPanelProps {
  readonly paymentId: string;
  /** 머리말 "목록으로" — 페이지가 결제 내역으로 옮긴다. */
  readonly onBack: () => void;
}

const RECEIPT_LINK_CLASS =
  'text-sm font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground';

/**
 * 결제 상세 — 머리말(결제 상태 배지·목록으로)·결제 정보·환불 이력·환불 폼을 묶은 자립 블록(결제 설계 §12).
 * 받기 전·실패는 카드 안 가운데 안내로 보인다.
 */
export const PaymentDetailPanel = ({
  paymentId,
  onBack,
}: PaymentDetailPanelProps): ReactElement => {
  const detailQuery = usePaymentDetailQuery(paymentId);
  const invalidate = useInvalidatePayments();
  const orders = useInvalidateOrders();
  const orderId = detailQuery.data?.orderId;
  // 환불·다시 시도는 성공이든 오류든 끝나면 다시 불러온다 — 토스가 거절하면 서버가 환불 행을 FAILED 로 남기고
  // 오류를 던지므로, 성공 때만 불러오면 이력 표가 서버 상태와 어긋난다.
  // 재조회 promise 를 돌려줘 요청이 재조회까지 진행 중으로 남는다 — 새 잔액이 오기 전에 [환불하기]가 풀리면
  // 직전 금액·사유로 두 번째 환불을 보낼 수 있다.
  // 같은 결제 tx 가 주문을 REFUNDED·취소 요청을 ACCEPTED 로 바꿀 수 있어(주문 설계 §6-2) 그 주문의 상세·주문 목록·견적 화면도
  // 무효화한다 — 열려 있지 않은 화면은 다시 요청하지 않고 다음에 열 때 읽는다.
  const refresh = (): Promise<unknown> =>
    Promise.all([
      invalidate.detail(paymentId),
      invalidate.list(),
      orderId === undefined ? undefined : orders.detail(orderId),
      orders.list(),
      orders.quotes(),
    ]);
  const cancelAction = useCancelPayment({ onSettled: refresh });
  const retryAction = useRetryPaymentCancel({ onSettled: refresh });
  const payment = detailQuery.data;

  const handleRefund = (input: { readonly amount: number; readonly reason: string }): void => {
    if (
      !window.confirm(`${formatWon(input.amount)}을 환불하시겠습니까? 환불은 되돌릴 수 없습니다.`)
    ) {
      return;
    }

    retryAction.reset();
    cancelAction.cancel({ paymentId, ...input });
  };

  const handleRetry = (cancelId: string): void => {
    cancelAction.reset();
    retryAction.retry(cancelId);
  };

  let body: ReactElement;
  if (detailQuery.isPending) {
    body = <EmptyState>불러오는 중…</EmptyState>;
  } else if (payment === undefined) {
    // 알림(alert)은 오류 글만 읽게 하고, 다시 시도 버튼은 안내 상자 아래 형제로 둔다.
    body = (
      <div className="flex flex-col gap-4">
        <EmptyState isError>결제를 불러오지 못했습니다: {detailQuery.error?.message}</EmptyState>
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void detailQuery.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      </div>
    );
  } else {
    const canRefund =
      (payment.status === 'DONE' || payment.status === 'PARTIAL_CANCELED') &&
      payment.balanceAmount > 0;

    body = (
      <div className="flex flex-col gap-6">
        <ContentCard
          title="결제 정보"
          actions={
            payment.receiptUrl ? (
              <a
                href={payment.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={RECEIPT_LINK_CLASS}
              >
                토스 영수증 보기
              </a>
            ) : undefined
          }
        >
          <dl className={styles.list}>
            <dt className={styles.term}>주문 ID</dt>
            <dd>{payment.orderId}</dd>
            <dt className={styles.term}>주문명</dt>
            <dd>{payment.orderName}</dd>
            <dt className={styles.term}>회원</dt>
            <dd>{payment.userEmail}</dd>
            <dt className={styles.term}>금액</dt>
            <dd className="tabular-nums">{formatWon(payment.amount)}</dd>
            <dt className={styles.term}>남은 금액</dt>
            <dd className="tabular-nums">{formatWon(payment.balanceAmount)}</dd>
            <dt className={styles.term}>결제수단</dt>
            <dd>{payment.method ?? <span className={styles.empty}>-</span>}</dd>
            <dt className={styles.term}>승인 일시</dt>
            <dd>
              {payment.approvedAt ? (
                formatDateTime(payment.approvedAt)
              ) : (
                <span className={styles.empty}>-</span>
              )}
            </dd>
            <dt className={styles.term}>실패 사유</dt>
            <dd>
              {payment.failureMessage ? (
                `${payment.failureMessage} (${payment.failureCode ?? '-'})`
              ) : (
                <span className={styles.empty}>-</span>
              )}
            </dd>
          </dl>
        </ContentCard>

        <PaymentCancelsTable
          cancels={payment.cancels}
          isRetrying={retryAction.isPending}
          onRetry={handleRetry}
        />

        {retryAction.error ? (
          <p
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {retryAction.error.message}
          </p>
        ) : null}

        {canRefund ? (
          <RefundForm
            key={payment.balanceAmount}
            balanceAmount={payment.balanceAmount}
            isPending={cancelAction.isPending}
            error={cancelAction.error?.message ?? null}
            onSubmit={handleRefund}
          />
        ) : null}
      </div>
    );
  }

  return (
    <>
      <PaymentDetailHeader status={payment?.status ?? null} onBack={onBack} />
      {body}
    </>
  );
};
